import {
  Box,
  Button,
  Flex,
  Heading,
  Skeleton,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useToast,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React from 'react';
import { useHistory, useParams } from 'react-router-dom';

import {
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';

import { Deliverables } from '../CreditAgreementFullArtifact/section/Deliverables';
import {
  errorToastConfig,
  errorToastId,
} from 'packages/common-frontend/constants';
import { useGetCreditFacilityQuery } from 'packages/credit-facility/requests/requests.gql.generated';
import { useGetCreditAgreementArtifactQuery } from 'packages/creditAgreements/requests/requests.gql.generated';
import { BorrowingBaseMechanics } from 'packages/creditAgreements/ui/CreditAgreementArtifact/sections/BorrowingBaseMechanics';
import { ReportingDeliverables } from 'packages/creditAgreements/ui/CreditAgreementArtifact/sections/ReportingDeliverables';
import { CreditAgreementDownload } from 'packages/creditAgreements/ui/CreditAgreementDownload';
import { parseCreditAgreementFullArtifact } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { CreditAgreementContextProvider } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/hooks';
import { FacilityMechanics } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/section/FacilityMechanics';
import { FinancialCovenants } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/section/FinancialCovenants';
import { FundingRequestMechanics } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/section/FundingRequestMechanics';
import { PortfolioCovenants } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/section/PortfolioCovenants';
import { SettlementMechanics } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/section/SettlementMechanics';
import { FiArrowLeft } from 'react-icons/fi';

const CreditAgreementViewHeader: React.FC<{
  creditFacilityId: string;
}> = ({ creditFacilityId }) => {
  const history = useHistory();

  const {
    data: creditFacilityData,
    loading: creditFacilityLoading,
  } = useGetCreditFacilityQuery({
    variables: {
      creditFacilityId,
    },
  });

  if (
    creditFacilityLoading ||
    !creditFacilityData ||
    !creditFacilityData.creditFacility
  ) {
    return (
      <Skeleton height="50px" data-testid="credit-agreement-header-loading" />
    );
  }

  const {
    creditFacility: { slug: creditFacilitySlug },
  } = creditFacilityData;

  return (
    <Flex mt="30px">
      <Button
        variant="ghost"
        onClick={() => {
          history.push('/credit-agreements/');
        }}
      >
        <FiArrowLeft />
      </Button>
      <Box>
        <Heading as="h2">Credit Agreement</Heading>
        <Text mt="10px">
          {commonUtils.creditFacilitySlugToName(creditFacilitySlug)}
        </Text>
      </Box>
    </Flex>
  );
};

export const CreditAgreement: React.FC<{}> = () => {
  const { creditAgreementId } = useParams<{ creditAgreementId: string }>();

  const toast = useToast();
  const history = useHistory();

  const {
    data: creditAgreementData,
    loading: creditAgreementLoading,
  } = useGetCreditAgreementArtifactQuery({
    variables: { creditAgreementId },
    onError: () => {
      if (!toast.isActive(errorToastId)) {
        errorToastConfig.title = `Error loading credit agreement.`;
        toast(errorToastConfig);
      }
    },
    fetchPolicy: 'network-only',
  });

  if (
    creditAgreementLoading ||
    !creditAgreementData ||
    !creditAgreementData.creditAgreementArtifact
  ) {
    return (
      <Skeleton height="600px" data-testid="credit-agreement-route-loading" />
    );
  }

  const {
    creditAgreementArtifact: { creditAgreement, id },
  } = creditAgreementData;

  let parsedCreditAgreement = null;

  try {
    parsedCreditAgreement = parseCreditAgreementFullArtifact(creditAgreement);
    finleyAssert(
      parsedCreditAgreement,
      `Expect creditAgreementFullArtifact to exist for creditAgreementId ${id}`,
    );

    const {
      borrowingBaseMechanics,
      creditFacilityId,
      fundingRequestMechanics,
      facilityMechanics,
      settlementMechanics,
      financialCovenants,
      portfolioCovenants,
      reportingDeliverables,
      deliverables,
    } = parsedCreditAgreement;

    let routeLevelError = false;
    return (
      <Box className="main">
        <Sentry.ErrorBoundary
          onError={() => {
            if (!routeLevelError && !toast.isActive(errorToastId)) {
              errorToastConfig.title = `Failed to load credit agreement page`;
              routeLevelError = true;
              toast(errorToastConfig);
              history.push('/credit-agreements');
            }
          }}
        >
          <Box>
            <CreditAgreementViewHeader creditFacilityId={creditFacilityId} />
            <Tabs>
              <TabList>
                <Tab>Overview</Tab>
                <Tab>Documents</Tab>
              </TabList>

              <TabPanels>
                <TabPanel>
                  <FacilityMechanics facilityMechanics={facilityMechanics} />
                  <CreditAgreementContextProvider
                    initOrgSlugs={[]}
                    parties={facilityMechanics.parties}
                  >
                    <FundingRequestMechanics
                      fundingRequestMechanics={fundingRequestMechanics}
                    />
                  </CreditAgreementContextProvider>
                  <SettlementMechanics
                    settlementMechanics={settlementMechanics}
                  />
                  <BorrowingBaseMechanics
                    borrowingBaseMechanics={borrowingBaseMechanics}
                  />
                  <FinancialCovenants financialCovenants={financialCovenants} />
                  <PortfolioCovenants portfolioCovenants={portfolioCovenants} />
                  {/* Deliverable v3 */}
                  <ReportingDeliverables
                    reportingDeliverables={reportingDeliverables}
                  />
                  {/* Deliverable v4 */}
                  <Deliverables deliverables={deliverables} />
                </TabPanel>
                <TabPanel>
                  <CreditAgreementDownload
                    creditFacilityId={creditFacilityId}
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>
        </Sentry.ErrorBoundary>
      </Box>
    );
  } catch (err) {
    Sentry.captureException(err);
    errorToastConfig.title = `Error parsing credit agreement artifact for credit agreement ${id}`;
    toast(errorToastConfig);
    history.push('/credit-agreements');
  }
  throw new Error('unreachable code to make linter happy');
};
